import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData } from '@trinity/chat';
import { Edit2 } from 'react-feather';
import { DataTableSettings } from './DataTableSettings';

interface DataTableSettingsButtonProps {
  currentChart: QuickChartData | null;
  onSettingsSave: (updatedChart: QuickChartData & { type: 'data-table' }) => void;
}

export const DataTableSettingsButton = ({ currentChart, onSettingsSave }: DataTableSettingsButtonProps) => {
  // Data Table Settings Modal State
  const [dataTableSettingsOpen, setDataTableSettingsOpen] = useState(false);
  const [currentDataTableChart, setCurrentDataTableChart] = useState<QuickChartData & { type: 'data-table' } | null>(null);

  const openDataTableSettings = useCallback(() => {
    if (currentChart?.type === 'data-table') {
      setCurrentDataTableChart(currentChart);
      setDataTableSettingsOpen(true);
    }
  }, [currentChart]);

  const closeDataTableSettings = useCallback(() => {
    setDataTableSettingsOpen(false);
    setCurrentDataTableChart(null);
  }, []);

  const handleDataTableSettingsSave = useCallback((updatedChart: QuickChartData & { type: 'data-table' }) => {
    onSettingsSave(updatedChart);
    setCurrentDataTableChart(updatedChart);
  }, [onSettingsSave]);

  // Only render if current chart is a data table
  if (currentChart?.type !== 'data-table') {
    return null;
  }

  return (
    <>
      <EditButton onClick={openDataTableSettings} title="Edit Data Table Settings">
        <StyledIcon />
      </EditButton>

      {dataTableSettingsOpen && currentDataTableChart && (
        <DataTableSettings
          open={dataTableSettingsOpen}
          onClose={closeDataTableSettings}
          chartData={currentDataTableChart}
          onSave={handleDataTableSettingsSave} />
      )}
    </>
  );
};

const EditButton = styled.div`
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #666;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`;

const StyledIcon = styled(Edit2)(({ theme }) => ({
  width: 22,
  height: 22,
  display: 'flex',
  flexShrink: 0,
  strokeWidth: 3,
  color: theme.palette.wl?.enabled ? theme.palette.wl.primary.main : theme.palette.sentiment.primary.main,
}));